import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Star,
  Heart,
} from "lucide-react";
import SmoHead from "../assets/smoHead.jpg";

export default function About() {
  return (
    <section className="bg-pink-50 py-10 px-4 sm:px-6 md:px-12 lg:px-20 xl:px-32">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start gap-3 mb-12">
          <div className="w-[4px] min-h-[100px] bg-gradient-to-b from-pink-500 to-purple-500 rounded-sm" />
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              About us
            </h2>
            <p className="text-gray-500 italic text-base sm:text-lg mt-1">
              Hear from our happy customers across the globe.
            </p>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-10 items-start">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Brand Introduction */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-pink-100 hover:shadow-md transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Heart className="text-rose-400 w-6 h-6 mr-3" />
                ShubhyaMakeOvvers
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4 text-sm sm:text-base">
                Welcome to{" "}
                <span className="font-semibold text-pink-600">
                  ShubhyaMakeOvvers
                </span>
                , we believe that beauty is an art, and every face is a unique
                canvas. Founded by Ruchi K, a Lakmé Certified Makeup Artist and
                Cosmetologist, our studio is dedicated to enhancing natural
                beauty with a touch of elegance and expertise. With a passion
                for creating flawless looks for every occasion from bridal
                makeovers to casual styling, Ruchi combines professional skills
                with a deep understanding of individual needs. At Shubhya
                Makeovvers, we don’t just do makeup; we craft confidence and
                bring your best version to life.
              </p>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Our salon is built on the foundation of enhancing your natural
                beauty while celebrating your unique personality. We believe
                that true beauty comes from confidence, and our mission is to
                help you discover and embrace your most radiant self.
              </p>

              {/* Smaller Image */}
              <div className="relative overflow-hidden rounded-2xl mt-6 group shadow-lg bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-500 max-w-sm mx-auto">
                <img
                  src={SmoHead}
                  alt="Owner-image"
                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                <div className="absolute top-4 left-4 w-6 h-6 bg-pink-300/30 backdrop-blur-sm rounded-full border border-pink-300/50 opacity-0 group-hover:opacity-100 transition-all duration-700 transform group-hover:scale-110" />
                <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-110" />
              </div>
            </div>

            {/* Mission & Values */}
            <div className="bg-gradient-to-br from-pink-100 to-rose-100 p-6 sm:p-8 rounded-2xl border border-pink-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <Star className="text-pink-500 w-5 h-5 mr-3" />
                Our Mission & Values
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                We are committed to providing exceptional beauty services that
                go beyond expectations. Our customer-first approach ensures that
                every visit is a personalized experience tailored to your
                individual needs and preferences.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                With a highly skilled and friendly team of beauty professionals,
                we combine traditional techniques with modern trends to create
                looks that are both timeless and contemporary.
              </p>
            </div>

            {/* Services */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-pink-100">
              <h3 className="text-xl font-bold text-gray-800 mb-6">
                Our Specialized Services
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  {
                    name: "Bridal Makeup",
                    desc: "Your perfect wedding day look",
                  },
                  {
                    name: "Skincare Treatments",
                    desc: "Rejuvenating facial therapies",
                  },
                  { name: "Hair Styling", desc: "Creative cuts and styling" },
                  {
                    name: "Personal Grooming",
                    desc: "Complete beauty makeovers",
                  },
                ].map((service, index) => (
                  <div
                    key={index}
                    className="bg-pink-50 p-4 rounded-lg border border-pink-100 hover:bg-pink-100 transition-colors duration-200"
                  >
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {service.name}
                    </h4>
                    <p className="text-sm text-gray-600">{service.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Quality Note */}
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-6 rounded-xl border border-rose-200">
              <p className="text-gray-700 text-center leading-relaxed text-sm sm:text-base">
                <span className="font-semibold text-pink-600">
                  Premium Quality Promise:
                </span>{" "}
                We exclusively use high-end beauty products from trusted brands
                to ensure the best results and care for your skin and hair.
              </p>
            </div>
          </div>

          {/* Right Column - Contact Info */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-pink-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Get in Touch
              </h3>

              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start space-x-4 p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition">
                  <Phone className="text-pink-500 w-6 h-6 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Phone</h4>
                    <a
                      href="tel:+917673-7236"
                      className="text-pink-600 font-medium hover:text-pink-700 text-sm"
                    >
                      +91 7673-7236
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4 p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition">
                  <Mail className="text-pink-500 w-6 h-6 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Email</h4>
                    <a
                      href="mailto:subhyamakeovvers@gmail.com"
                      className="text-pink-600 font-medium hover:text-pink-700 text-sm break-all"
                    >
                      subhyamakeovvers@gmail.com
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start space-x-4 p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition">
                  <MapPin className="text-pink-500 w-6 h-6 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">
                      Location
                    </h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Mansarovar, Jaipur,
                      <br />
                      Rajasthan, India
                    </p>
                  </div>
                </div>

                {/* Timings */}
                <div className="flex items-start space-x-4 p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition">
                  <Clock className="text-pink-500 w-6 h-6 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">
                      Opening Hours
                    </h4>
                    <p className="text-gray-700 text-sm">
                      <strong>Mon – Sat:</strong> 10am – 8pm
                      <br />
                      <span className="text-xs text-gray-500">
                        Closed on Sundays
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Button */}
              <div className="mt-8 text-center">
                <button className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-6 py-3 rounded-full font-semibold hover:scale-105 hover:from-pink-600 hover:to-rose-600 transition-all duration-200 shadow-lg hover:shadow-xl">
                  Book Your Appointment
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div className="text-center mt-16 p-6 bg-gradient-to-r from-pink-100 via-rose-50 to-pink-100 rounded-2xl border border-pink-200">
          <p className="text-lg text-gray-700 italic font-light max-w-3xl mx-auto leading-relaxed">
            "At ShubhyaMakeOvvers, we don't just create beautiful looks – we
            create beautiful experiences. Your journey to radiant beauty begins
            with us."
          </p>
          <div className="flex justify-center mt-4 space-x-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="text-yellow-400 w-5 h-5 fill-current" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
